
import { clearAllCookie, delCookie, getCookie, setCookie } from '@raft/utils';
import { BaseUser } from './base-user';
import { AuthUser, ProUserInfo } from './types';
import {
  BUGLY_LOGIN_STORAGE_KEY,
  genAvatar,
  genCookieName,
  TDS_SASS_TOKEN,
  TDS_SASS_UIN,
  TDS_TOKEN,
  TDS_USER_ID,
  TDS_USER_NAME,
} from './utils';

const defaultAuthInfo: AuthUser = {
  isLoggedIn: false,
};
/**
 * 不需要校验登录态的页面路由配置
 */
const noNeedCheckLoginList = [
  '/login',
  '/register',
  '/select-account',
  '/security',
  '/index/main',
  '/index/tds-main',
  '/index/rightly',
  '/',
  '/alert-notice',
  // 大疆 SSO 登录页
  '/login/dji',
];

export const ROOT_DOMAIN = '.tds.qq.com';

/**
 * 删除passport种的cookie
 */
export function clearLoginCookies() {
  delCookie(genCookieName(TDS_TOKEN), '/', ROOT_DOMAIN);
  delCookie(genCookieName(TDS_SASS_TOKEN), '/', ROOT_DOMAIN);
  delCookie(genCookieName(TDS_SASS_UIN), '/', ROOT_DOMAIN);
  delCookie(genCookieName(TDS_USER_ID), '/', ROOT_DOMAIN);
  delCookie(genCookieName(TDS_USER_NAME), '/', ROOT_DOMAIN);
}

const PASSPORT_LOGIN_EXPIRES_IN_DAY = 30;

const PASSPORT_LOGIN_PARAM_MAP = {
  token: 'tds_token',
  sassToken: 'tds_sassToken',
  sassUin: 'tds_sassUin',
  userId: 'tds_userId',
  userName: 'tds_userName',
};

const clearPassportLoginParamsFromUrl = () => {
  const url = new URL(window.location.href);
  Object.values(PASSPORT_LOGIN_PARAM_MAP).forEach(param => url.searchParams.delete(param));
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
};

const syncPassportLoginParamsToCookie = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get(PASSPORT_LOGIN_PARAM_MAP.token);

  if (!token) {
    return;
  }

  const sassToken = params.get(PASSPORT_LOGIN_PARAM_MAP.sassToken) || '';
  const sassUin = params.get(PASSPORT_LOGIN_PARAM_MAP.sassUin) || '';
  const userId = params.get(PASSPORT_LOGIN_PARAM_MAP.userId) || '';
  const userName = params.get(PASSPORT_LOGIN_PARAM_MAP.userName) || '';

  setCookie(genCookieName(TDS_TOKEN), token, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/');

  if (sassToken) {
    setCookie(genCookieName(TDS_SASS_TOKEN), sassToken, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/');
  }

  if (sassUin) {
    setCookie(genCookieName(TDS_SASS_UIN), sassUin, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/');
  }

  if (userId) {
    setCookie(genCookieName(TDS_USER_ID), userId, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/');
  }

  if (userName) {
    setCookie(genCookieName(TDS_USER_NAME), userName, PASSPORT_LOGIN_EXPIRES_IN_DAY, '/');
  }

  if (userId || userName) {
    localStorage.setItem(BUGLY_LOGIN_STORAGE_KEY, JSON.stringify({
      buglyId: userId,
      buglyUserId: userId,
      name: userName,
      buglyUserName: userName,
    }));
  }

  clearPassportLoginParamsFromUrl();
};

/**
 * 大疆版用户实现
 *
 * 登录走大疆 SSO（OAuth 2.0 授权码模式）：
 * - logout 跳转 /login/dji?url=<原URL>，登录页把 url 作为 OAuth state 携带
 * - 大疆回调带 code → 登录页调 /v5/api/login(platform=dji_oauth) → 后端用 code+secret 换 token
 * - 也兼容 tds-passport 已完成登录后的回跳参数，落成本域 cookie 后继续按登录态校验
 * - 登录态 cookie 与 pro 版一致（tds_token/tds_uid/tds_username）
 */
export default class DjiUser implements BaseUser<DjiUser> {
  onLogin(): boolean {
    return true;
  }
  logout(): void {
    localStorage.removeItem(BUGLY_LOGIN_STORAGE_KEY);
    clearAllCookie();
    clearLoginCookies();
    // 跳转大疆 SSO 登录页，原URL通过 ?url= 传递，登录页再作为 OAuth state 携带
    const currentUrl = encodeURIComponent(location.href);
    window.location.href = `${location.origin}/login/dji?url=${currentUrl}`;
  }
  checkLogin(pathname: string): boolean {
    const authInfo = this.initUserInfo();
    const noNeedCheck = noNeedCheckLoginList.includes(pathname);
    if (!noNeedCheck && !authInfo.isLoggedIn) {
      this.logout();
      return false;
    }

    return true;
  }

  initUserInfo(): AuthUser {
    try {
      syncPassportLoginParamsToCookie();
      const token = this.getBuglyToken();
      return token ? {
        isLoggedIn: true,
        token,
        saasToken: this.getSaasToken(),
        userInfo: this.getUserInfo() as ProUserInfo,
      } : defaultAuthInfo;
    } catch (e) {
      return defaultAuthInfo;
    }
  }
  getUserInfo(): DjiUser | null {
    try {
      return JSON.parse(localStorage.getItem(BUGLY_LOGIN_STORAGE_KEY) as string) as DjiUser;
    } catch (e) {
      return null;
    }
  }
  getAvatar(): string {
    const userName = this.getUserName();
    return genAvatar(userName);
  }

  public getUserName(): string {
    return decodeURIComponent(getCookie(genCookieName(TDS_USER_NAME)) || '');
  }

  public getUserId() {
    return getCookie(genCookieName(TDS_USER_ID)) || '';
  }

  public getSaasToken() {
    return getCookie(genCookieName(TDS_SASS_TOKEN)) || '';
  }

  public getBuglyToken() {
    return getCookie(genCookieName(TDS_TOKEN)) || '';
  }

  public getSassUin() {
    return getCookie(genCookieName(TDS_SASS_UIN)) || '';
  }
}
